/* @flow */
import * as React from 'react';
import idx from 'idx';
import { graphql, compose } from 'react-apollo';
import update from 'immutability-helper';

import GET_GROUP_MEMBER_QUERY from './queries/GroupMemberProfile.gql';
import Notice from 'components/Notice';
import UsersProfileContainer from './UsersProfileContainer';
import GroupMemberActionsMenuButton from 'user/components/GroupMemberActionsMenuButton';
import EventTracking from 'lib/EventTracking';

import type {
  GroupMemberProfile_classes,
  GroupMemberProfile_classes_membersPaginated_members,
} from './queries/__generated__/GroupMemberProfile';
import type { Props as UsersProfileContainerProps } from 'users/components/UsersProfile/UsersProfileContainer';

export function shapeMemberIntoUserProfile(
  member: ?GroupMemberProfile_classes_membersPaginated_members
) {
  if (!member) {
    return null;
  }

  return update(
    {
      ...member,
      ...(member.user || {}),
      roles: [member.user.role],
    },
    { $unset: ['user', 'role'] }
  );
}

type Props = UsersProfileContainerProps & { group: GroupMemberProfile_classes };

class UsersProfileInGroupContainer extends React.Component<Props> {
  trackEvent = (key: string, properties: ?Object = {}) => {
    EventTracking.trackKey(`class_people_list.${key}`, {
      group_id: this.props.group.id,
      ...properties,
    });
  };

  renderErrorBanner = () => {
    const { loading, loaded } = this.props;

    if (!loading && !loaded) {
      return (
        <Notice color="polar-bear">
          This person is not a member of this class so you don’t have permission to view them.
        </Notice>
      );
    }

    return null;
  };

  renderUserActionsMenuButton = () => {
    const { group, member, onClose, onUserChanged } = this.props;

    return (
      <GroupMemberActionsMenuButton
        group={group}
        member={member}
        onUserRemoved={() => {
          this.trackEvent('user_profile.org_membership.removed');
          onClose();
          onUserChanged();
        }}
        onUserUpdate={() => {
          this.trackEvent('user_profile.role.updated');
          onUserChanged();
        }}
      />
    );
  };

  render() {
    return (
      <UsersProfileContainer
        {...this.props}
        userActionsMenuButton={this.renderUserActionsMenuButton()}
        errorBanner={this.renderErrorBanner()}
        trackEvent={this.trackEvent}
      />
    );
  }
}

const withGroupMember = graphql(GET_GROUP_MEMBER_QUERY, {
  options: props => {
    const { member, entityUuid } = props;

    return {
      variables: {
        groupUuid: entityUuid,
        userId: member.id,
      },
      fetchPolicy: 'network-only',
    };
  },
  props: ({ data, ownProps }) => {
    const loadedMember = shapeMemberIntoUserProfile(
      idx(data, _ => _.classes[0].membersPaginated.members[0])
    );
    return {
      member: data.loading ? ownProps.member : loadedMember || ownProps.member,
      group: idx(data, _ => _.classes[0]),
      loading: data.loading,
      loaded: !!loadedMember,
      error: data.error,
      refetch: data.refetch,
    };
  },
  skip: ({ entityUuid }) => !entityUuid,
});

export default compose(withGroupMember)(UsersProfileInGroupContainer);
